import { Navigate, useSearchParams, type RouteObject } from "react-router"
import { insightsShown } from "./insights/shown.ts"
import { Layout } from "./layouts/layout.tsx"
import { RouteError, ShellError } from "./layouts/route-error.tsx"
import NotFound from "./pages/not-found.tsx"

export const routes: RouteObject[] = [
  // Where Beacon's authorization server sends an agent's sign-in. Outside the shell: each is one
  // card on the canvas, and each finds out for itself who is signed in.
  {
    path: "oauth/sign-in",
    errorElement: <ShellError />,
    lazy: async () => {
      const Component = await import("./connected-agents/sign-in-page.tsx")
      return { Component: Component.default }
    },
  },
  {
    path: "oauth/consent",
    errorElement: <ShellError />,
    lazy: async () => {
      const Component = await import("./connected-agents/consent-page.tsx")
      return { Component: Component.default }
    },
  },
  // Where an invitation's link lands: setting a password, before anyone is signed in.
  {
    path: "welcome",
    errorElement: <ShellError />,
    lazy: async () => {
      const Component = await import("./sign-in/welcome-page.tsx")
      return { Component: Component.default }
    },
  },
  {
    path: "/",
    element: <Layout />,
    errorElement: <ShellError />,
    children: [
      {
        // A page that fails shows so inside the panel, with the sidebar still there to leave by.
        errorElement: <RouteError />,
        children: [
          {
            index: true,
            lazy: async () => {
              const Component = await import("./pages/home.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "projects",
            lazy: async () => {
              const Component = await import("./pages/projects.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "my-tickets",
            lazy: async () => {
              const Component = await import("./pages/my-tickets.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat",
            lazy: async () => {
              const Component = await import("./pages/chat.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/search",
            lazy: async () => {
              const Component = await import("./pages/chat-search.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/threads",
            lazy: async () => {
              const Component = await import("./pages/chat-threads.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/inbox",
            lazy: async () => {
              const Component = await import("./pages/chat-inbox.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/mentions",
            lazy: async () => {
              const Component = await import("./pages/chat-mentions.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/saved",
            lazy: async () => {
              const Component = await import("./pages/chat-saved.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/notifications",
            lazy: async () => {
              const Component = await import("./pages/chat-notifications.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/beacon",
            lazy: async () => {
              const Component = await import("./pages/chat-beacon.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "chat/:conversationId",
            lazy: async () => {
              const Component = await import("./pages/chat-conversation.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "my-project",
            lazy: async () => {
              const Component = await import("./pages/my-project.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "projects/:key",
            lazy: async () => {
              const Component = await import("./pages/project-board.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "projects/:key/:number",
            lazy: async () => {
              const Component = await import("./pages/project-ticket.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "projects/:key/epics",
            lazy: async () => {
              const Component = await import("./pages/project-epics.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "projects/:key/change-requests",
            lazy: async () => {
              const Component = await import("./pages/project-change-requests.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "projects/:key/change-requests/new",
            lazy: async () => {
              const Component = await import("./pages/new-change-request.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "projects/:key/change-requests/:number",
            lazy: async () => {
              const Component = await import("./pages/change-request.tsx")
              return { Component: Component.default }
            },
          },
          // Production shows no Insights until their history is whole (BEA-297).
          ...(insightsShown
            ? [
                {
                  path: "projects/:key/insights",
                  lazy: async () => {
                    const Component = await import("./pages/project-insights.tsx")
                    return { Component: Component.default }
                  },
                },
              ]
            : []),
          {
            path: "projects/:key/settings",
            lazy: async () => {
              const Component = await import("./pages/project-settings.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "documents",
            lazy: async () => {
              const Component = await import("./pages/documents.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "documents/:id",
            lazy: async () => {
              const Component = await import("./pages/document.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "profile",
            lazy: async () => {
              const Component = await import("./pages/profile.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "connections",
            lazy: async () => {
              const Component = await import("./pages/my-connectors.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "connections/:connectorId/tools",
            lazy: async () => {
              const Component = await import("./pages/connector-tools.tsx")
              return { Component: Component.default }
            },
          },
          {
            // A review a Member's agent waits on them for, linked from Slack by its id alone.
            path: "reviews/:reviewId",
            lazy: async () => {
              const Component = await import("./pages/connector-review.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "connect-an-agent",
            lazy: async () => {
              const Component = await import("./pages/connect-an-agent.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "people",
            lazy: async () => {
              const Component = await import("./pages/people.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "people/:memberId",
            lazy: async () => {
              const Component = await import("./pages/person.tsx")
              return { Component: Component.default }
            },
          },
          // People replaced Members: an old link lands there, a Member's detail on their profile.
          { path: "members", element: <MembersMoved /> },
          {
            path: "groups",
            lazy: async () => {
              const Component = await import("./pages/groups.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "agent",
            lazy: async () => {
              const Component = await import("./pages/agent.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "spend",
            lazy: async () => {
              const Component = await import("./pages/spend.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "github",
            lazy: async () => {
              const Component = await import("./pages/github.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "custom-emoji",
            lazy: async () => {
              const Component = await import("./pages/custom-emoji.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "brands",
            lazy: async () => {
              const Component = await import("./pages/brands.tsx")
              return { Component: Component.default }
            },
          },
          // Why the design system's page exists and ships in every stage: its own header comment.
          // It renders in the real shell, so its demos sit where every page does, and loads
          // lazily, so the whole primitive set stays out of the bundle other pages pay for.
          {
            path: "kitchen-sink",
            lazy: async () => {
              const Component = await import("./pages/kitchen-sink.tsx")
              return { Component: Component.default }
            },
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  },
]

/** The Members page's old address: People, or the profile of the Member its `?member=` named. */
function MembersMoved() {
  const [params] = useSearchParams()
  const member = params.get("member")
  return <Navigate replace to={member ? `/people/${member}` : "/people"} />
}
